import { Button, ListItem, Text, UnorderedList } from '@chakra-ui/react';
import { PlusIcon } from '@sphere/icons';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { FC, useMemo, useState } from 'react';
import { AttributeValue } from '../useFacets';

type Props = {
  values: AttributeValue[];
  showCount?: boolean;
  addFilter: (facetValue: string) => void;
};

const ITEMS_PER_PAGE = 100;

const AttributeFacetSelection: FC<Props> = ({ values = [], addFilter, showCount = true }) => {
  const { t } = useTranslation('search');
  const [maxItems, setMaxItems] = useState(ITEMS_PER_PAGE);
  const renderedValues = useMemo(() => values.slice(0, maxItems), [values, maxItems]);

  if (!values.length) return null;

  return (
    <>
      <UnorderedList
        textAlign="left"
        ml={0}
        p={0}
        bg="gray.2"
        border="1px solid"
        rounded="md"
        borderColor="border"
      >
        {renderedValues.map(({ value, count }) => (
          <ListItem
            key={`unselected-value-${value}`}
            py="space.8"
            px="space.16"
            fontSize="sm"
            display="flex"
            justifyContent="space-between"
            cursor="pointer"
            onClick={() => addFilter(value)}
            color="gray.1"
            _hover={{
              color: 'gray.5',
            }}
            _first={{ mt: 'space.4' }}
            _last={{ mb: 'space.4' }}
            transition="color 120ms ease-in-out"
          >
            {`${value}`.replace(/_/g, ' ')}
            {showCount && (
              <Text ml="auto" pl="space.8" fontSize="xs" color="inherit">
                {count}
              </Text>
            )}
          </ListItem>
        ))}
        {values.length > maxItems && (
          <ListItem px="space.16" pb="space.4" _last={{ mb: 'space.4' }}>
            <Button
              variant="link"
              fontSize="sm"
              fontWeight="medium"
              onClick={() => setMaxItems(prevState => prevState + ITEMS_PER_PAGE)}
              minW="unset"
              leftIcon={<PlusIcon boxSize={rem(10)} />}
              iconSpacing="space.4"
            >
              {t('facets.actions.show-more')}
            </Button>
          </ListItem>
        )}
      </UnorderedList>
    </>
  );
};

export default AttributeFacetSelection;
